import type Sortable from 'sortablejs';
import { resetPutSortable } from 'sortablejs';

import { on } from '@disy/lib/event-util';

import { getStore } from 'cadenza/store/cadenza-store';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { SortableList } from 'cadenza/workbook/sortable-list/sortable-list';
import { getAttributeById } from 'cadenza/workbook/workbook-core-selectors.state';

const REPLACE_TARGET_LIST_CLASS = 'is-replace-target-list';
const REPLACE_TARGET_ITEM_CLASS = 'is-replace-target-item';

declare module 'sortablejs' {
  export function resetPutSortable(): void;
}

type DragLeaveEvent = Event & { fromElement: Element | null; toElement: Element | null };
type AddEvent = Event & { item: Element };

/**
 * SortableJS plugin to enable the replacement of attributes.
 * Note: this is not a general replace plugin, it has some logic specific to AttributeDropPanel
 * and attribute elements.
 *
 * When an attribute is dragged over an attribute element inside an AttributeDropPanel, and the CTRL
 * key is pressed, then when the attribute is dropped, the default "add" event behaviour is overridden,
 * and the existing attribute is replaced.
 *
 * The rest of the logic is in attribute-drop-panel.js.
 *
 * The plugin is enabled for attribute bundles in the view designer.
 */
export class ReplaceAttributePlugin {
  static readonly pluginName = 'disyReplaceAttribute';

  _sortable;
  _sortableListElement;
  _replacedElement: Element | null = null;

  constructor(sortable: Sortable, element: SortableList) {
    this._sortable = sortable;
    this._sortableListElement = element;

    on(element, 'dragleave', (event: DragLeaveEvent) => this._onDragleave(event));
    on(element, 'add', (event: AddEvent) => this._onAdd(event));
  }

  dragOver(event: { originalEvent: DragEvent }) {
    // Reset dragging over status to trigger validation check again, if the user has released the modifier key, see resetPutSortable for more details.
    if (this._replaceActive && !isReplaceModifierKeyDown(event.originalEvent)) {
      resetPutSortable();
      this._clearTargetListClass();
      this._clearTargetItemClasses();
    }
  }

  dragOverValid(event: { originalEvent: DragEvent; isOwner: boolean; dragEl: Element; target: Element }) {
    this._replacedElement = null;

    if (isReplaceModifierKeyDown(event.originalEvent) && !event.isOwner) {
      this._sortableListElement.classList.add(REPLACE_TARGET_LIST_CLASS);

      const draggedElement = event.dragEl;
      const targetElement = event.target;

      if (canDraggedElementReplaceTargetElement(targetElement, draggedElement)) {
        this._sortableListElement.dropDenied = false;
        targetElement.classList.add(REPLACE_TARGET_ITEM_CLASS);
        // because on Chrome on MacOS, the dragleave event is fired before the drop event,
        // we can't rely on finding the element by its CSS class (which is removed in the dragleave event handler),
        // so we need to store it in order to be able to use it in the _onAdd method
        // See CADENZA-21352
        this._replacedElement = targetElement;

        this._sortableListElement.dispatchEvent(
          new CustomEvent('dragOverReplace', {
            detail: {
              draggedElement,
              replacedElement: targetElement,
            },
          }),
        );
      }
    } else {
      this._clearTargetListClass();
      this._clearTargetItemClasses();
    }
  }

  _onAdd(event: AddEvent) {
    const replacedElement = this._replacedElement;

    if (replacedElement && this._replaceActive) {
      this._sortableListElement.dispatchEvent(
        new CustomEvent('replace', {
          detail: {
            draggedElement: event.item,
            replacedElement,
          },
        }),
      );
      event.preventDefault();
      this._clearTargetListClass();
    }

    // Prevent adding element if the user was holding the modifier key but dropped an item just between 2 items, so there was replaced element.
    if (this._replaceActive && !replacedElement) {
      event.preventDefault();
      event.item.remove();
    }
  }

  _onDragleave(event: DragLeaveEvent) {
    this._clearTargetItemClasses();

    if (
      event.fromElement === this._sortableListElement &&
      this._replaceActive &&
      !this._sortableListElement.contains(event.toElement)
    ) {
      this._clearTargetListClass();
    }
  }

  _clearTargetListClass() {
    this._sortableListElement.classList.remove(REPLACE_TARGET_LIST_CLASS);
  }

  _clearTargetItemClasses() {
    this._sortableListElement
      .querySelectorAll(`.${REPLACE_TARGET_ITEM_CLASS}`)
      .forEach((it) => it.classList.remove(REPLACE_TARGET_ITEM_CLASS));
  }

  get _replaceActive() {
    return this._sortableListElement.classList.contains(REPLACE_TARGET_LIST_CLASS);
  }
}

export function isReplaceModifierKeyDown(event: DragEvent) {
  return event.shiftKey;
}

function canDraggedElementReplaceTargetElement(targetElement: Element, draggedElement: Element) {
  if (!targetElement.classList.contains('d-attribute-tile')) {
    return false;
  }

  const targetAttribute = getAttributeById(getStore().getState(), targetElement.getAttribute('data-id') as AttributeId);
  const draggedAttribute = getAttributeById(
    getStore().getState(),
    draggedElement.getAttribute('data-id') as AttributeId,
  );

  // In the case of "virtual" attributes such as measures summary, there is no underlying attribute
  return !!targetAttribute && !!draggedAttribute;
}
